<template>
  <div class="comments">
    <h6 class="aside-title">Comments</h6>
    <form id="add-comments" @submit.prevent="addCommentHandler">
      <div class="form-group mt-3">
        <label class="sr-only" for="comments-textarea">Add Comment</label>
        <textarea
          v-model="messageData"
          class="form-control"
          id="comments-textarea"
          minlength="3"
          rows="6"
          placeholder="Write Your Comment"
        ></textarea>
        <div class="d-flex mt-3">
          <button
            :disabled="messageData.length === 0"
            type="submit"
            class="btn btn-success ml-auto"
          >
            Add Comment
          </button>
        </div>
      </div>
    </form>
    <ul class="list-unstyled mt-4">
      <li v-for="comment in commentsList" :key="comment.id">
        <div class="d-flex mb-2 align-items-center">
          <router-link
            v-if="comment.employer_name"
            :to="{
              name: 'employer_id',
              params: {
                employer_id: comment.employer_id,
                employer_name: comment.employer_profile_name,
              },
            }"
            >{{ `${comment.employer_profile_name} (${comment.employer_name})` }}</router-link
          >
          <router-link v-else to>{{ comment.owner_name }}</router-link>
          <span class="date ml-auto">
            {{
              comment.updated_at
                | formatDate({
                  format: getUserProfile.format_date,
                  timezone: getUserProfile.timezone,
                  hasHourAndMinutes: true,
                })
            }}
          </span>
        </div>

        <div v-if="comment" :style="comment.employer_name ? 'padding-bottom:12px;' : ''">
          <span :style="{ 'white-space': 'pre-line' }">
            {{ comment.data }}
          </span>
        </div>

        <div v-if="!comment.employer_name" class="d-flex mb-3" v-show="comment == activeEdit">
          <button
            v-show="comment != activeEdit"
            :id="comment.id"
            class="btn btn-primary ml-auto mr-2"
            @click="commentEdit(comment)"
            type="button"
          >
            <i class="fas fa-pencil-alt"></i>
          </button>

          <button
            type="button"
            v-show="comment != activeEdit"
            class="btn btn-danger"
            data-toggle="modal"
            :data-target="'#delete-comment-btn' + '-' + comment.id"
          >
            <i class="fas fa-times-circle"></i>
          </button>
          <!-- Portal-->
          <portal to="delete-comment"></portal>
          <div
            :id="'delete-comment-btn' + '-' + comment.id"
            class="modal fade"
            tabindex="-1"
            role="dialog"
            aria-labelledby="mainModalLabel"
            aria-hidden="true"
          >
            <div class="modal-dialog modal-dialog-centered modal-sm" role="document">
              <div class="modal-content">
                <div class="modal-header"></div>
                <div class="modal-body">
                  <p>Please confirm that you want to delete this comment</p>
                </div>
                <div class="modal-footer d-flex justify-content-center">
                  <button
                    class="btn btn-primary btn-custom"
                    @click="deleteCommentHandler(comment.id)"
                    data-dismiss="modal"
                  >
                    Delete
                  </button>
                  <button type="button" class="btn btn-secondary btn-custom" data-dismiss="modal">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <transition name="fade">
          <div v-show="comment == activeEdit">
            <textarea
              v-model="addedComment"
              class="form-control mb-3"
              id="comments-textarea"
              rows="7"
              placeholder="Edit Your Comment"
            ></textarea>
          </div>
        </transition>

        <transition name="fade">
          <div class="mb-3" v-if="comment == activeEdit">
            <span class="d-flex">
              <button
                type="button"
                class="btn btn-primary ml-auto mr-2"
                @click="doneEditCommentHandler(comment)"
              >
                Save
              </button>
              <button class="btn btn-danger" @click="activeEdit = null">Cancel</button>
            </span>
          </div>
        </transition>
      </li>
      <div class="no-comment-message" v-if="commentsList.length === 0">
        No comments have been registered.
      </div>
    </ul>

    <portal-target name="delete-comment"></portal-target>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  components: {},
  data: () => ({
    messageData: '',
    activeEdit: null,
    addedComment: '',
    errorMessage: '',
  }),
  props: {
    uuid: {
      type: String,
    },
    commentsList: {
      type: Array,
      default: () => [],
    },
  },
  methods: {
    addCommentHandler() {
      this.$emit('add-candidate-comment', this.messageData);
      this.messageData = '';
      this.activeEdit = null;
    },
    commentEdit(comment) {
      this.activeEdit = comment;
    },
    doneEditCommentHandler(comment) {
      if (!this.activeEdit) return;

      this.$emit('edit-comment', { id: comment.id, comment: this.addedComment });
      this.activeEdit = null;
    },
    deleteCommentHandler(commentId) {
      this.$emit('remove-comment', commentId);
      this.activeEdit = null;
    },
  },
  computed: {
    ...mapGetters(['getUserProfile']),
  },
  watch: {
    activeEdit(newComment) {
      if (newComment) {
        this.addedComment = newComment.data;
      }
    },
  },
  mounted() {},
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_variables';

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.comments {
  margin: 0px;
  h6 {
    font-size: 18px;
    font-weight: 700;
    display: none;
  }
}
.list-unstyled,
.form-group {
  font-family: $font-family-sans-lato;
  li {
    border-bottom: 1px solid #ddd;
    margin-bottom: 20px;
    a {
      text-decoration: none;
    }
    .date {
      font-size: 14px;
      background: none;
    }
    p {
      margin-bottom: 12px;
      font-size: 15px;
    }

    .btn-primary,
    .btn-secondary,
    .btn-danger {
      font-size: 14px;
      padding: 2px 8px;
      i {
        position: relative;
        top: 0px;
        font-size: 13px;
      }
    }
  }
}

.no-comment-message {
  font-size: 1.5rem;
  font-family: inherit;
  color: #a3a6b4;
  text-align: center;
  margin-top: 5rem;
}

@media only screen and (max-width: 992px) {
  .comments {
    padding: 0 16px;
  }
}

@media only screen and (max-width: 576px) {
  .comments {
    margin: 0;
    padding: 8px 3px 8px 3px !important;
    h6 {
      display: block;
    }
    form {
      margin: 17px 12px;
      .form-group {
        margin-top: 0 !important;
      }
    }
    .list-unstyled {
      margin: 10px 12px;
    }
  }
  .aside-title {
    display: block !important;
    margin-top: 22px !important;
  }
}
</style>
